import React, { useEffect, useState } from 'react';
import { ShieldCheck, Smartphone, KeyRound, Mail, ArrowLeft } from 'lucide-react';
import { tokenStorage } from '@/api/tokenManager';
import { MfaChallengeApi, MfaMethodType, TotpStart } from '@/services/mfa.service';
import { startAuthentication, startRegistration, isWebAuthnSupported } from '@/utils/webauthn';
import { TokenResponse } from '@/types/authentication.types';
import styles from './MfaChallenge.module.css';

import { tr } from '@/i18n';
import { localizeAuthMessage } from '@/auth/authMessages';
interface Props {
  mfaToken: string;
  allowed: string[];
  enrolled: string[];
  mustEnrol: boolean;
  emailHint?: string;
  /** Tokens are stored already; `res` tells whether a new password must be chosen next. */
  onComplete: (res: TokenResponse) => void;
  onCancel: () => void;
}

type Mode =
  | 'choose'
  | 'totp'
  | 'email'
  | 'passkey'
  | 'enroll-totp'
  | 'enroll-passkey';

const META: Record<MfaMethodType, { name: string; hint: string; Icon: React.FC<any> }> = {
  authenticator: { name: tr("ui.authenticator_app", "Authenticator app"), hint: tr("ui.enter_a_code_from_your_app", "Enter a code from your app"), Icon: Smartphone },
  passkey: { name: tr("ui.passkey_security_key", "Passkey / security key"), hint: tr("ui.use_your_device_or_security_key", "Use your device or security key"), Icon: KeyRound },
  email_token: { name: tr("ui.email_code", "Email code"), hint: tr("ui.get_a_one_time_code_by_email", "Get a one-time code by email"), Icon: Mail },
};

export const MfaChallenge: React.FC<Props> = ({
  mfaToken,
  allowed,
  enrolled,
  mustEnrol,
  emailHint,
  onComplete,
  onCancel,
}) => {
  // Verify options = enrolled methods. Enrol options (forced setup) = allowed
  // authenticator/passkey only (email never needs enrolment).
  const verifyOptions = (enrolled as MfaMethodType[]).filter((m) => META[m]);
  const enrolOptions = (allowed as MfaMethodType[]).filter(
    (m) => m === 'authenticator' || m === 'passkey',
  );
  const options = mustEnrol ? enrolOptions : verifyOptions;

  // Map a method key to its panel mode (verify vs enrol).
  const verifyMode = (m: MfaMethodType): Mode =>
    m === 'authenticator' ? 'totp' : m === 'email_token' ? 'email' : 'passkey';
  const enrolMode = (m: MfaMethodType): Mode =>
    m === 'passkey' ? 'enroll-passkey' : 'enroll-totp';

  const initialMode: Mode =
    options.length === 1
      ? mustEnrol
        ? enrolMode(options[0])
        : verifyMode(options[0])
      : 'choose';

  const [mode, setMode] = useState<Mode>(initialMode);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [totpSetup, setTotpSetup] = useState<TotpStart | null>(null);

  const finish = (res: TokenResponse) => {
    tokenStorage.setTokens(res.access_token, res.refresh_token);
    onComplete(res);
  };

  const pick = (m: MfaMethodType) => {
    setError('');
    setCode('');
    // Method keys ('authenticator', 'email_token') are not panel modes ('totp',
    // 'email'): picking from the chooser used to land on no panel at all when
    // a person had more than one method (UAT DEF-AU-008).
    setMode(mustEnrol ? enrolMode(m) : verifyMode(m));
  };

  // Auto-send email code when entering the email panel.
  useEffect(() => {
    if (mode === 'email' && !emailSent) {
      (async () => {
        try {
          setBusy(true);
          await MfaChallengeApi.emailSend(mfaToken);
          setEmailSent(true);
        } catch {
          setError(tr("ui.could_not_send_the_email_code", "Could not send the email code."));
        } finally {
          setBusy(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Kick off TOTP enrolment (fetch secret + QR) when entering enroll panel.
  useEffect(() => {
    if (mode === 'enroll-totp' && !totpSetup) {
      (async () => {
        try {
          setBusy(true);
          setTotpSetup(await MfaChallengeApi.enrollTotpStart(mfaToken));
        } catch {
          setError(tr("ui.could_not_start_authenticator_setup", "Could not start authenticator setup."));
        } finally {
          setBusy(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const verifyTotp = async () => {
    setBusy(true);
    setError('');
    try {
      finish(await MfaChallengeApi.totpVerify(mfaToken, code));
    } catch (e: any) {
      setError(localizeAuthMessage(e?.response?.data?.detail) || tr("ui.invalid_code", "Invalid code."));
      setBusy(false);
    }
  };

  const verifyEmail = async () => {
    setBusy(true);
    setError('');
    try {
      finish(await MfaChallengeApi.emailVerify(mfaToken, code));
    } catch (e: any) {
      setError(localizeAuthMessage(e?.response?.data?.detail) || tr("ui.invalid_or_expired_code", "Invalid or expired code."));
      setBusy(false);
    }
  };

  const doPasskey = async () => {
    setBusy(true);
    setError('');
    try {
      const opts = await MfaChallengeApi.passkeyOptions(mfaToken);
      const credential = await startAuthentication(opts);
      finish(await MfaChallengeApi.passkeyVerify(mfaToken, credential));
    } catch (e: any) {
      setError(localizeAuthMessage(e?.response?.data?.detail) || e?.message || tr("ui.passkey_verification_failed", "Passkey verification failed."));
      setBusy(false);
    }
  };

  const enrollTotpVerify = async () => {
    setBusy(true);
    setError('');
    try {
      finish(await MfaChallengeApi.enrollTotpVerify(mfaToken, code));
    } catch (e: any) {
      setError(localizeAuthMessage(e?.response?.data?.detail) || tr("ui.invalid_code", "Invalid code."));
      setBusy(false);
    }
  };

  const enrollPasskey = async () => {
    setBusy(true);
    setError('');
    try {
      const opts = await MfaChallengeApi.enrollPasskeyOptions(mfaToken);
      const credential = await startRegistration(opts);
      finish(await MfaChallengeApi.enrollPasskeyVerify(mfaToken, credential));
    } catch (e: any) {
      setError(localizeAuthMessage(e?.response?.data?.detail) || e?.message || tr("ui.could_not_create_a_passkey", "Could not create a passkey."));
      setBusy(false);
    }
  };

  const resendEmail = async () => {
    setError('');
    try {
      setBusy(true);
      await MfaChallengeApi.emailSend(mfaToken);
      setEmailSent(true);
    } catch {
      setError(tr("ui.could_not_resend_the_code", "Could not resend the code."));
    } finally {
      setBusy(false);
    }
  };

  const back = () => {
    setError('');
    setCode('');
    setMode('choose');
  };

  const Header = ({ title, sub }: { title: string; sub: string }) => (
    <div className={styles.header}>
      <div className={styles.shield}><ShieldCheck size={26} /></div>
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.subtitle}>{sub}</p>
    </div>
  );

  return (
    <div className={styles.wrap}>
      {/* CHOOSE */}
      {mode === 'choose' && (
        <>
          <Header
            title={mustEnrol ? tr("ui.set_up_two_factor", "Set up two-factor") : tr("ui.two_factor_authentication", "Two-factor authentication")}
            sub={
              mustEnrol
                ? tr("ui.your_administrator_requires_a_second_factor_set_00e42e", "Your administrator requires a second factor. Set one up to continue.")
                : tr("ui.choose_how_to_verify_your_identity", "Choose how to verify your identity.")
            }
          />
          <div className={styles.methodList}>
            {options.map((m) => {
              const meta = META[m];
              const Icon = meta.Icon;
              if (m === 'passkey' && !isWebAuthnSupported()) return null;
              return (
                <button key={m} type="button" className={styles.methodBtn} onClick={() => pick(m)}>
                  <span className={styles.methodIcon}><Icon size={19} /></span>
                  <span className={styles.methodMeta}>
                    <span className={styles.methodName}>{meta.name}</span>
                    <span className={styles.methodHint}>{meta.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className={styles.footerRow}>
            <button type="button" className={styles.linkBtn} onClick={onCancel}>
              <ArrowLeft size={13} style={{ verticalAlign: 'middle' }} /> {tr("ui.back_to_sign_in", "Back to sign in")}
            </button>
          </div>
        </>
      )}

      {/* TOTP verify */}
      {mode === 'totp' && (
        <>
          <Header title={tr("ui.authenticator_code", "Authenticator code")} sub={tr("ui.enter_the_6_digit_code_from_your_authenticator_a_ad10d8", "Enter the 6-digit code from your authenticator app.")} />
          <div className={styles.field}>
            <input
              className={styles.codeInput}
              inputMode="numeric"
              autoFocus
              maxLength={6}
              placeholder="••••••"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && code.length >= 6 && verifyTotp()}
            />
          </div>
          {error && <div className={styles.error}>{error}</div>}
          <button className={styles.primaryBtn} disabled={busy || code.length < 6} onClick={verifyTotp}>
            {busy ? <span className={styles.spinner} /> : tr("ui.verify", "Verify")}
          </button>
          <Footer options={options} back={back} onCancel={onCancel} />
        </>
      )}

      {/* Email verify */}
      {mode === 'email' && (
        <>
          <Header title={tr("ui.email_code", "Email code")} sub={tr("ui.we_sent_a_6_digit_code_to_x", "We sent a 6-digit code to {v0}.", { v0: emailHint || 'your email' })} />
          <div className={styles.field}>
            <input
              className={styles.codeInput}
              inputMode="numeric"
              autoFocus
              maxLength={6}
              placeholder="••••••"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && code.length >= 6 && verifyEmail()}
            />
          </div>
          {error && <div className={styles.error}>{error}</div>}
          <button className={styles.primaryBtn} disabled={busy || code.length < 6} onClick={verifyEmail}>
            {busy ? <span className={styles.spinner} /> : tr("ui.verify", "Verify")}
          </button>
          <div className={styles.footerRow}>
            <button type="button" className={styles.linkBtn} onClick={resendEmail} disabled={busy}>
              {tr("ui.resend_code", "Resend code")}
            </button>
            {options.length > 1 && (
              <button type="button" className={styles.linkBtn} onClick={back}>
                {tr("ui.use_another_method", "Use another method")}
              </button>
            )}
          </div>
        </>
      )}

      {/* Passkey verify */}
      {mode === 'passkey' && (
        <>
          <Header title={tr("ui.passkey", "Passkey")} sub={tr("ui.use_your_device_biometrics_or_security_key_to_ve_923d7b", "Use your device biometrics or security key to verify.")} />
          {error && <div className={styles.error}>{error}</div>}
          <button className={styles.primaryBtn} disabled={busy} onClick={doPasskey}>
            {busy ? <span className={styles.spinner} /> : (<><KeyRound size={17} /> {tr("ui.use_passkey", "Use passkey")}</>)}
          </button>
          <Footer options={options} back={back} onCancel={onCancel} />
        </>
      )}

      {/* Enroll TOTP */}
      {mode === 'enroll-totp' && (
        <>
          <Header title={tr("ui.set_up_authenticator", "Set up authenticator")} sub={tr("ui.scan_the_qr_code_with_your_authenticator_app_the_ecb0d8", "Scan the QR code with your authenticator app, then enter the 6-digit code.")} />
          {totpSetup ? (
            <div className={styles.qrBox}>
              <img src={totpSetup.qr} alt={tr("ui.authenticator_qr_code", "Authenticator QR code")} />
              <div className={styles.secretRow}>{totpSetup.secret}</div>
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}><span className={styles.spinner} /></div>
          )}
          <div className={styles.field}>
            <input
              className={styles.codeInput}
              inputMode="numeric"
              maxLength={6}
              placeholder="••••••"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && code.length >= 6 && enrollTotpVerify()}
            />
          </div>
          {error && <div className={styles.error}>{error}</div>}
          <button className={styles.primaryBtn} disabled={busy || code.length < 6} onClick={enrollTotpVerify}>
            {busy ? <span className={styles.spinner} /> : tr("ui.confirm_continue", "Confirm & continue")}
          </button>
          <Footer options={options} back={back} onCancel={onCancel} />
        </>
      )}

      {/* Enroll passkey */}
      {mode === 'enroll-passkey' && (
        <>
          <Header title={tr("ui.create_a_passkey", "Create a passkey")} sub={tr("ui.register_this_device_or_a_security_key_as_your_s_1f793d", "Register this device or a security key as your second factor.")} />
          {error && <div className={styles.error}>{error}</div>}
          <button className={styles.primaryBtn} disabled={busy} onClick={enrollPasskey}>
            {busy ? <span className={styles.spinner} /> : (<><KeyRound size={17} /> {tr("ui.create_passkey", "Create passkey")}</>)}
          </button>
          <Footer options={options} back={back} onCancel={onCancel} />
        </>
      )}
    </div>
  );
};

const Footer: React.FC<{ options: any[]; back: () => void; onCancel: () => void }> = ({
  options,
  back,
  onCancel,
}) => (
  <div className={styles.footerRow}>
    <button type="button" className={styles.linkBtn} onClick={onCancel}>
      <ArrowLeft size={13} style={{ verticalAlign: 'middle' }} /> {tr("ui.sign_in", "Sign in")}
    </button>
    {options.length > 1 && (
      <button type="button" className={styles.linkBtn} onClick={back}>
        {tr("ui.use_another_method", "Use another method")}
      </button>
    )}
  </div>
);
